import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useMutationEndVacationMode } from '../../queries/user/hooks/useMutationEndVacationMode';
import { useMutationSetVacationMode } from '../../queries/user/hooks/useMutationSetVacationMode';
import { useQueryUser } from '../../queries/user/hooks/useQueryUser';
import { useQueryVacationHistory } from '../../queries/user/hooks/useQueryVacationHistory';
import { pageTitleSx } from '../../constants/designTokens';

const VacationMode: React.FunctionComponent = () => {
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [error, setError] = useState('');

  const { data: meData, loading: meLoading, refetch } = useQueryUser({
    fetchPolicy: 'network-only',
  });
  const { data: historyData, loading: historyLoading, refetch: refetchHistory } =
    useQueryVacationHistory({
      fetchPolicy: 'network-only',
    });

  const [setVacationMode, { loading: setLoading }] = useMutationSetVacationMode();
  const [endVacationMode, { loading: endLoading }] = useMutationEndVacationMode();

  const me = meData?.me;
  const isActive = Boolean(me?.vacationIsActive);

  const canSubmit = useMemo(() => {
    return Boolean(startAt && endAt && !setLoading);
  }, [startAt, endAt, setLoading]);

  const onSetVacation = async () => {
    setError('');
    if (!startAt || !endAt) {
      setError('Please select start and end dates.');
      return;
    }
    if (new Date(endAt) <= new Date(startAt)) {
      setError('End date must be after start date.');
      return;
    }

    await setVacationMode({
      variables: {
        data: {
          startAt: new Date(`${startAt}T00:00:00.000Z`).toISOString(),
          endAt: new Date(`${endAt}T23:59:59.999Z`).toISOString(),
        },
      },
    });
    await Promise.all([refetch(), refetchHistory()]);
  };

  const onEndVacation = async () => {
    setError('');
    await endVacationMode({ variables: { data: {} } });
    await Promise.all([refetch(), refetchHistory()]);
  };

  if (meLoading) {
    return <CircularProgress sx={{ position: 'absolute', left: '50%', top: '50%' }} color="inherit" />;
  }

  return (
    <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography component="h1" sx={pageTitleSx}>
        Vacation Mode
      </Typography>

      <Alert severity={isActive ? 'warning' : 'success'}>
        <Typography component="div">
          Vacation mode is currently <b>{isActive ? 'Active' : 'Inactive'}</b>.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          When Vacation Mode is active, all currently active postings are temporarily set to inactive for the selected
          period. Ongoing orders continue as usual. Once Vacation Mode ends, postings deactivated by Vacation Mode are
          automatically restored to their previous status.
        </Typography>
      </Alert>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          type="date"
          label="Start date"
          InputLabelProps={{ shrink: true }}
          value={startAt}
          onChange={(e) => setStartAt(e.target.value)}
        />
        <TextField
          type="date"
          label="End date"
          InputLabelProps={{ shrink: true }}
          value={endAt}
          onChange={(e) => setEndAt(e.target.value)}
        />
        <Button variant="contained" disabled={!canSubmit} onClick={onSetVacation}>
          {setLoading ? 'Saving...' : 'Set Vacation'}
        </Button>
        <Button variant="outlined" color="error" disabled={!isActive || endLoading} onClick={onEndVacation}>
          {endLoading ? 'Ending...' : 'End vacation period'}
        </Button>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Vacation History
        </Typography>
        {historyLoading ? (
          <CircularProgress size={18} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(historyData?.myVacationHistory || []).map((item: any) => (
                <TableRow key={item.id}>
                  <TableCell>{moment(item.startAt).format('MM/DD/YYYY HH:mm')}</TableCell>
                  <TableCell>{moment(item.endAt).format('MM/DD/YYYY HH:mm')}</TableCell>
                  <TableCell>{item.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Paper>
  );
};

export default VacationMode;

